import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  Chip, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, Alert, IconButton, Tooltip,
  LinearProgress, Stack, Card, CardContent, Grid
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { jobsAPI } from '../services/api';
import JobDetailDialog from '../components/jobs/JobDetailDialog';

const JOB_STATUSES = {
  'pending': { color: 'default', label: 'Pending', icon: <PendingIcon fontSize="small" /> },
  'processing': { color: 'primary', label: 'Processing', icon: <HourglassTopIcon fontSize="small" /> },
  'completed': { color: 'success', label: 'Completed', icon: <CheckCircleOutlineIcon fontSize="small" /> },
  'failed': { color: 'error', label: 'Failed', icon: <ErrorOutlineIcon fontSize="small" /> }
};

const JOB_TYPES = {
  'text_generation': { label: 'Text Generation' },
  'audio_generation': { label: 'Audio Generation' }
};

const JobsManagement = () => {
  const navigate = useNavigate(); // Add navigate hook
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [jobStatsCounts, setJobStatsCounts] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getAllJobs(page + 1, rowsPerPage, statusFilter || null);
      
      setJobs(response.data.jobs || []);
      setTotalCount(response.data.meta?.total || 0);
      
      const stats = {
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0
      };
      
      response.data.jobs.forEach(job => {
        if (stats[job.status] !== undefined) {
          stats[job.status]++;
        }
      });
      
      setJobStatsCounts(stats);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [page, rowsPerPage, statusFilter]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchJobs();
  };

  const handleViewDetails = async (jobId) => {
    try {
      setLoadingDetails(true);
      const response = await jobsAPI.getJobById(jobId);
      
      setCurrentJob(response.data);
      setDetailsOpen(true);
    } catch (err) {
      console.error('Error fetching job details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderStatusChip = (status) => {
    const statusInfo = JOB_STATUSES[status] || { color: 'default', label: status, icon: null };
    return (
      <Chip
        icon={statusInfo.icon}
        label={statusInfo.label}
        size="small"
        color={statusInfo.color}
        variant="outlined"
      />
    );
  };

  const renderEntityId = (job) => {
    // For theme_blocks_generation jobs
    if (job.type === 'block_generation' && job.params) {
      const courseId = job.params.courseId;
      const themeId = job.params.themeId;
      
      if (courseId && themeId) {
        return (
          <Link 
            to={`/courses/${courseId}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/courses/${courseId}`, { 
                state: { activeThemeId: themeId, activeTab: 1 } 
              });
            }}
            style={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            {courseId.substring(0, 4)}.../{themeId.substring(0, 4)}...
          </Link>
        );
      }
    }
    
    // Existing code for text_generation jobs
    if (!job.entityId) return 'N/A';
    
    const truncatedId = job.entityId.substring(0, 8) + '...';
    
    if (job.type === 'text_generation') {
      return (
        <Link 
          to={`/books/${job.entityId}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'primary.main', textDecoration: 'underline' }}
        >
          {truncatedId}
        </Link>
      );
    }
    
    // For all other job types
    return truncatedId;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Generation Jobs
        </Typography>
        <IconButton onClick={handleRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.entries(JOB_STATUSES).map(([status, info]) => (
          <Grid item xs={6} sm={3} key={status}>
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{info.label}</Typography>
                  {info.icon}
                </Stack>
                <Typography variant="h5" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {jobStatsCounts[status] || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">All Jobs</Typography>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
              displayEmpty
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(JOB_STATUSES).map(([value, { label }]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && jobs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Entity ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>{job.id.substring(0, 8)}...</TableCell>
                        <TableCell>{JOB_TYPES[job.type]?.label || job.type}</TableCell>
                        <TableCell>{renderStatusChip(job.status)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <LinearProgress
                              variant="determinate"
                              value={job.progressPercent}
                              sx={{ width: 100, mr: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {job.progressPercent}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(job.createdAt)}</TableCell>
                        <TableCell>{formatDate(job.updatedAt)}</TableCell>
                        <TableCell>
                          {renderEntityId(job)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDetails(job.id)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" color="text.secondary" py={3}>
                          No jobs found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Use the JobDetailDialog component */}
      <JobDetailDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        job={currentJob}
        loading={loadingDetails}
      />
    </Box>
  );
};

export default JobsManagement;
